<script>
    // -- IMPORTS

    import { enabledAdminView } from '$lib/store/adminStore.js';
  import { onMount } from 'svelte';
    import Loading from './Loading.svelte';

    // -- VARIABLES

    /** @type {{
        * onClick?: () => void
        * toggleMouseHover?: () => void
        * fullWidth?: boolean, 
        * invertColor?: boolean, 
        * disabled?: boolean, 
        * loading?: boolean, 
        * shouldShowCheckbox?: boolean,
        * checkboxValue?: boolean,
        * target?: string, 
        * type?: "button" | "submit",
        * size?: "default" | "compact",
        * variant?: "danger" | "no-animation" | "default" | "ghost", 
        * leftIconName?: string,
        * rightIconName?: string,
        * iconColor?: string,
        * iconSize?: "90" | "100" | "150" | "200" | "250" | "300" | "350" | "400" | "600"
        * href?: string, 
        * children?: import('svelte').Snippet, 
        * style?: any,
     *  }} */
    let {
        onClick = () => {},
        toggleMouseHover = () => {},
        fullWidth = false,
        invertColor = false,
        disabled = false,
        shouldShowCheckbox = false,
        checkboxValue = false,
        loading = false,
        type = 'button',
        size = 'default',
        variant = 'default',
        target = '_self',
        leftIconName = '',
        rightIconName = '',
        iconColor = '',
        iconSize = '150',
        href = null,
        style = null,
        children
    } = $props();

    if( iconColor === '' )
    {
        iconColor = invertColor 
                        ? 'dark-grey' 
                        : 'light-gold';
    }

    onMount(() => {
        if( variant === 'danger' )
        {
            iconColor = 'red-500'
        }
    })
</script>

<style lang="stylus">
    // -- IMPORTS

    @import '../../../constant.styl';
    @import '../../../mixin.styl';
    @import '../../../class.styl';

    // -- ELEMENTS

    button,
    a
    {
        border: 1px solid lightGoldColor;
        border-radius: 0;
        padding: .875rem 1.125rem;

        display: flex;
        gap: 1rem;
        justify-content: center;
        align-items: center;

        background-color: darkGreyColor;

        font-size: 1rem;
        text-transform: uppercase;
        color: lightGoldColor;

        cursor: pointer;
        text-wrap: nowrap;
        width: fit-content;

        transition: all 0.2s ease-in-out;

        +media( smaller-desktop )
        {
            width: 100%;
            max-width: unset;
        }
    }

    button[disabled]
    {
        pointer-events: none;
        cursor: not-allowed;
    }

    button.compact
    a.compact
    {
        padding: 0.5rem 0.625rem;

        gap: 0.4rem;

        border-radius: 0.4rem
    }

    button.full-width,
    a.full-width,
    {
        width: 100%;
        max-width: unset;
    }

    button.invert-color,
    a.invert-color
    {
        border: 1px solid lightGoldColor;

        background: lightGoldColor;

        color: darkGreyColor;
    }

    .ghost
    {
        background: transparent;
        border: none;
    }

    .ghost .button-icon,
    .ghost:hover .button-icon,
    .ghost:focus .button-icon
    {
        background-color: lightGoldColor;
    }
    
    .ghost:hover,
    .ghost:focus
    {
        background: transparent;
        border: none;
    }

    button:hover,
    button:focus,
    a:hover,
    a:focus
    {
        background-color: lightGoldColor;

        color: darkGreyColor;
    }

    button:hover .button-icon,
    button:focus .button-icon,
    a:hover .button-icon,
    a:focus .button-icon
    {
        background: darkGreyColor;
    }

    button.invert-color:hover,
    button.invert-color:focus,
    a.invert-color:hover,
    a.invert-color:focus
    {
        background-color: darkGreyColor;

        color: lightGoldColor;
    }

    button.invert-color:hover .button-icon,
    button.invert-color:focus .button-icon,
    a.invert-color:hover .button-icon,
    a.invert-color:focus .button-icon
    {
        background: lightGoldColor;

        transition: .2s
    }

    button.invert-color:hover .checkbox-container,
    button.invert-color:focus .checkbox-container,
    .checkbox-container
    {
        position: relative;

        width: 1rem;
        height: 1rem;

        border: 1px solid lightGoldColor;
    }  

    button.invert-color .checkbox-container
    button:hover .checkbox-container,
    button:focus .checkbox-container
    {
        border: 1px solid darkGreyColor;
    }

    .button-check-icon
    {
        position: absolute;

        top: -10px;
        left: -8px;
    }

    button.no-animation:hover,
    button.no-animation:focus,
    {
        background-color: darkGreyColor;

        color: lightGoldColor;
    }

    button:disabled,
    button[disabled],
    button[disabled]:hover
    {
        cursor: not-allowed;
    }

    // -- CLASSES

    .danger
    {
        border-color:  redColor500;

        background: darkGreyColor;

        color: redColor500;
    }

    .danger:hover,
    .danger:focus
    {
        background: redColor500;

        color: darkGreyColor;
    }

    .danger .button-icon
    {
        background-color: redColor500;
    }

    .danger:hover .button-icon,
    .danger:focus .button-icon
    {
        background-color: darkGreyColor;
    }
</style>

{#if href}
    <a 
        class={ `${variant} ${size}` } 
        href={ href } 
        class:full-width={ fullWidth } 
        class:invert-color={ invertColor } 
        style={ style } { target } 
        onclick={ onClick } 
        class:is-admin-enabled={ $enabledAdminView }
        onmouseenter={ toggleMouseHover }
        onmouseleave={ toggleMouseHover }
    >
        {@render children?.()}
    </a>
{:else}
    <button 
        class={ `${variant} ${size}` } 
        class:full-width={ fullWidth } 
        class:invert-color={ invertColor } 
        class:is-admin-enabled={ $enabledAdminView }
        style={ style } 
        type={ type } 
        disabled={ loading || disabled } 
        onclick={ onClick } 
        onmouseenter={ toggleMouseHover }
        onmouseleave={ toggleMouseHover }
    >
        {#if shouldShowCheckbox}
            <div class="checkbox-container">
                {#if checkboxValue}
                    <div class="light-gold-check-icon size-200 button-check-icon button-icon"></div>
                {/if}
            </div>
        {/if}
        
        {#if leftIconName}
            <div class={`${iconColor}-${leftIconName}-icon size-${ iconSize } button-icon`}></div>
        {/if }

        {#if loading}
            <Loading size="1.5rem"/>
        {:else}
            {@render children?.()}
        {/if}

        {#if rightIconName}
            <div class={`${iconColor}-${rightIconName}-icon size-${ iconSize } button-icon`}></div>
        {/if }
    </button>
{/if}

